/* eslint-disable react/prop-types */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import portalService, { getPortalSession, onSessionEnded } from '@/services/portal/portalService';

/**
 * The signed-in guest and the firm's branding, for every client portal page
 * (OL-192). Kept apart from the staff AuthProvider: a guest is not a user and
 * never holds a staff token.
 */
const PortalContext = createContext(null);

const DEFAULT_BRANDING = { firm_name: null, logo: null, hide_oak_features: false };

const PortalProvider = ({ children }) => {
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [guest, setGuest] = useState(null);
  // True until a stored session (if any) has been checked.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalService
      .getBranding()
      .then(setBranding)
      .catch(() => setBranding(DEFAULT_BRANDING));
  }, []);

  const loadGuest = useCallback(async ({ quiet = false } = {}) => {
    if (!getPortalSession()) {
      setGuest(null);
      return null;
    }
    try {
      const me = await portalService.getMe({ quiet });
      setGuest(me);
      return me;
    } catch {
      setGuest(null);
      return null;
    }
  }, []);

  useEffect(() => {
    loadGuest({ quiet: true }).finally(() => setLoading(false));
  }, [loadGuest]);

  // The server ended the session: idle too long, signed out elsewhere, or
  // disabled by the firm. Back to the sign-in page, saying why.
  useEffect(() => {
    onSessionEnded(() => {
      setGuest(null);
      toast.info('You were signed out.', {
        description: 'Sessions end after 30 minutes without activity, to keep your documents secure.',
      });
      navigate('/portal/login', { replace: true });
    });
    return () => onSessionEnded(null);
  }, [navigate]);

  const signIn = useCallback(
    async (email, password) => {
      await portalService.login(email, password);
      return loadGuest();
    },
    [loadGuest],
  );

  const setPasswordFromLink = useCallback(
    async (token, password, confirmation) => {
      await portalService.setPassword(token, password, confirmation);
      return loadGuest();
    },
    [loadGuest],
  );

  const signOut = useCallback(async () => {
    try {
      await portalService.logout();
    } catch {
      /* the session is cleared locally either way */
    }
    setGuest(null);
    navigate('/portal/login', { replace: true });
  }, [navigate]);

  const value = useMemo(
    () => ({
      branding: branding ?? DEFAULT_BRANDING,
      brandingLoaded: branding !== null,
      guest,
      loading,
      signIn,
      setPasswordFromLink,
      signOut,
    }),
    [branding, guest, loading, signIn, setPasswordFromLink, signOut],
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

const usePortal = () => useContext(PortalContext);

export { PortalProvider, usePortal };
